import { useEffect, useState } from 'react';
import { getStoreInformation } from '@api/interfaces/storeApi';
import Config from '@config';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { AxfoodStoreInfoViewModel } from '@occ/api-client';
import useStore from '@hooks/useStore';
import useLocalStorage from '@hooks/useLocalStorage';
import { buildUrl } from '@helpers/buildUrl';
import paths from '@constants/paths';
import { trackOfflineStoreSet } from '@helpers/analyticsHelpers/trackStore';

const logToNR = (message: string) => {
  if (typeof window.newrelic !== 'undefined') {
    const { newrelic } = window;
    newrelic.noticeError(new Error(`PromotionPageRouting: ${message}`));
  }
};

const usePromotionPageRouting = (mode: PromotionTabType | undefined) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { store } = useStore();
  const [promotionTabLocalStorage, setPromotionTabLocalStorage] = useLocalStorage(
    Config.LOCAL_STORAGE_SELECTORS.promotionTab,
    ''
  );
  const [promotionStoreId, setPromotionStoreId] = useLocalStorage(Config.LOCAL_STORAGE_SELECTORS.promotionStoreId, '');
  const query = Object.fromEntries(searchParams?.entries() || []);

  const VALID_MODES = /^(ehandel|butik|preview)$/;
  const VALID_RESOLVED_MODES = /^(ehandel|butik|online-.*|offline-.*|preview)$/;
  const pathnameSegment = pathname.startsWith(paths.OFFERS) ? (pathname.split('/').pop() ?? '') : '';
  const currentPathnameMode = VALID_MODES.test(pathnameSegment) ? (pathnameSegment as PromotionTabType) : undefined;

  const [lastValidTab, setLastValidTab] = useState<PromotionTabType | undefined>(currentPathnameMode ?? mode);
  useEffect(() => {
    if (currentPathnameMode) setLastValidTab(currentPathnameMode);
  }, [currentPathnameMode]);
  const pathnameMode = lastValidTab;

  // True only when the URL is directly on a tab page (not a product modal intercept)
  const isOnTabRoute = !!currentPathnameMode;

  const [asyncTab, setAsyncTab] = useState<PromotionTabType>(pathnameMode);
  const activeTab = currentPathnameMode ?? pathnameMode ?? asyncTab;

  const [selectedOfflineStore, setSelectedOfflineStore] = useLocalStorage<AxfoodStoreInfoViewModel>(
    Config.LOCAL_STORAGE_SELECTORS.promotionOfflineStore,
    {} as AxfoodStoreInfoViewModel
  );

  const updatePromotionTab = (tab: string) => {
    setPromotionTabLocalStorage(tab);
  };

  const [shouldOpenFlyer, setShouldOpenFlyer] = useState(false);

  const lookUpAndSetStore = (
    storeId: string,
    restQuery: Record<string, string>,
    onSuccess?: () => void,
    onExternal?: () => void
  ) => {
    setPromotionStoreId(storeId);
    getStoreInformation(storeId)
      .then((res) => {
        if (!res?.external) {
          trackOfflineStoreSet('from_query_param', res.storeId);
          setSelectedOfflineStore(res);
          onSuccess?.();
        } else {
          router.replace(buildUrl(`${paths.OFFERS}/${Config.ROUTES.promotionpage.ecommerce}`, restQuery));
          onExternal?.();
        }
      })
      .catch(() => {
        setSelectedOfflineStore({} as AxfoodStoreInfoViewModel);
        setPromotionStoreId('');
      });
  };

  useEffect(() => {
    const { name, mode: queryMode, productCode, openFlyer, StoreID: queryStoreId, ...restQuery } = query;
    const localStorageMode = promotionTabLocalStorage as PromotionTabType;

    // Product modal intercept — underlying tab page is still mounted, do nothing
    const isOnOffersRoot = pathname === paths.OFFERS;
    if (!isOnTabRoute && pathnameMode && !isOnOffersRoot) return;

    // If the URL already has a valid tab segment, just persist to localStorage.
    // Exception: if a StoreID param is present and we already have a saved store/tab,
    // redirect to strip the StoreID from the URL (email deep-link cases 2 & 3).
    if (isOnTabRoute) {
      if (openFlyer === 'true') setShouldOpenFlyer(true);
      if (
        queryStoreId &&
        ((localStorageMode === 'ehandel' && store?.storeId && store.activelySelected) ||
          (localStorageMode === 'butik' && (promotionStoreId || selectedOfflineStore?.storeId)))
      ) {
        if (promotionStoreId !== queryStoreId) {
          logToNR(`Case 2: Using saved store ID: ${promotionStoreId}, different from store ID in URL: ${queryStoreId}`);
        } else {
          logToNR(`Case 3: Saved store ID: ${promotionStoreId}, same as store ID in URL`);
        }
        router.replace(buildUrl(`${paths.OFFERS}/${localStorageMode}`, restQuery));
        return;
      }
      /* Email deep-link case 4: /erbjudanden/butik?StoreID=... with no saved store → look up and open flyer */
      if (queryStoreId && currentPathnameMode === 'butik' && !promotionStoreId && !selectedOfflineStore?.storeId) {
        logToNR(`Case 4: StoreID in tab URL with no saved store: ${queryStoreId}`);
        lookUpAndSetStore(queryStoreId as string, restQuery, () => setShouldOpenFlyer(true));
        router.replace(buildUrl(`${paths.OFFERS}/${currentPathnameMode}`, restQuery));
        updatePromotionTab(currentPathnameMode);
        return;
      }
      // Only redirect if there are params to strip (name, mode, productCode, openFlyer, StoreID)
      // If only restQuery params remain (like q=...), the URL is already correct — no redirect needed
      const hasParamsToStrip = name || queryMode || productCode || openFlyer;
      if (hasParamsToStrip) {
        router.replace(buildUrl(`${paths.OFFERS}/${currentPathnameMode}`, restQuery));
      }
      updatePromotionTab(currentPathnameMode!);
      return;
    }
    const resolvedMode = (queryMode || name || localStorageMode) as PromotionTabType;

    /* If resolved mode doesn't match any known pattern, redirect to root */
    if (resolvedMode && !VALID_RESOLVED_MODES.test(`${resolvedMode}`)) {
      router.replace(paths.OFFERS);
      return;
    }

    /* Email deep-link case 1: StoreID in URL, no saved store yet → look up and set */
    if (queryStoreId && resolvedMode === 'butik' && !promotionStoreId && !selectedOfflineStore?.storeId) {
      logToNR(`Case 1: Using store ID from URL: ${queryStoreId}`);
      lookUpAndSetStore(
        queryStoreId as string,
        restQuery,
        () => {
          setAsyncTab(resolvedMode);
          updatePromotionTab(resolvedMode as string);
        },
        () => {
          setAsyncTab(Config.ROUTES.promotionpage.ecommerce as PromotionTabType);
          updatePromotionTab(Config.ROUTES.promotionpage.ecommerce);
        }
      );
      return;
    }

    /* Redirect to resolved mode, or fall back to /ehandel */
    const target = resolvedMode || (Config.ROUTES.promotionpage.ecommerce as PromotionTabType);
    router.replace(buildUrl(`${paths.OFFERS}/${target}`, restQuery));
    setAsyncTab(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, pathname, searchParams]);

  return {
    activeTab,
    selectedOfflineStore,
    setSelectedOfflineStore,
    shouldOpenFlyer,
  };
};

export default usePromotionPageRouting;
